import {
  File,
  InitialFields,
  Signatory,
  Signature,
  SignFields,
} from "@/type/core/dashboard/dashboard.types";
import {
  SignatureRequirementMode,
  TransactionType,
} from "@/type/core/transaction/transaction.types";
import { PDFDocument, PDFForm, PDFName, PDFSignature } from "pdf-lib";
import { blobToBase64, getBase64Async } from "@utils/helpers/base64-helper";
import { SignaturePatternEnum } from "@utils/enums/signature/signature.pattern.enum";
import { t } from "i18next";
import { MAX_FILE_NAME_LENGTH } from "../Constant";
import { toastError } from "./toast-helper";

interface BodyRequirement {
  transactionType: TransactionType;
  files: File[];
  signatories: Signatory[];
  signatures: Signature[];
  signatureRequirementMode: SignatureRequirementMode;
}

interface SignFieldsFormData {
  signFields: SignFields[];
}

interface FileUpload {
  selectedFiles: FileList;
  maxCount?: number;
  currentFileCount?: number;
  accept: string;
  extensions?: string[];
  maxSize?: number;
  maxSizeOfAllFiles?: number;
  currentDocumentsSize: number;
}

export class FileHelper {
  static removeNameDoublon = (files: File[]): File[] => {
    return files.reduce((acc: File[], current) => {
      if (!acc.some((item) => item.name === current.name)) {
        acc.push(current);
      }
      return acc;
    }, []);
  };

  // static calculatePeriod = (length: number, size: number): number => {
  //   if (length > 0 && size > 0) {
  //     const period = 1 + Math.floor(length / 10) + Math.floor(size / 1572864);
  //     const maxIntervalPeriod = 20;
  //     return period < maxIntervalPeriod ? period : maxIntervalPeriod;
  //   }
  //   return 1;
  // };

  static createUploadFilesFormData = (
    bodyRequirement: BodyRequirement,
    isFromModel: boolean = false,
  ): FormData => {
    const {
      transactionType,
      files,
      signatories,
      signatures,
      signatureRequirementMode,
    } = bodyRequirement;
    const body = new FormData();
    if (!isFromModel && signatureRequirementMode) {
      body.append("signatureRequirementMode", signatureRequirementMode);
    }
    const initialFieldsArray: InitialFields[] = [];
    const signFieldsArray: SignFieldsFormData[] = [];
    files.forEach((file, key) => {
      if (!file.file) {
        return;
      }
      const signFields: SignFieldsFormData = { signFields: [] };
      const initialFields: InitialFields | undefined = file.initialFields;
      if (transactionType === "signature") {
        signatures
          .filter(
            (signature) => signature.rank === key + 1 && signature.isMoved,
          )
          .map((signature) => {
            const signatoryOrder =
              signatories.findIndex(
                (signatory) => signatory.email === signature.email,
              ) + 1;
            signFields.signFields.push({
              positionX: (signature.positionX ?? 0) / window.devicePixelRatio,
              positionY: (signature.positionY ?? 0) / window.devicePixelRatio,
              signaturePriorityRule: signature.signaturePriorityRule,
              signaturePictureRank: signature.signaturePictureRank,
              page: signature.page,
              order: signatoryOrder,
              pattern: signature.pattern,
              withoutLogo:
                signature.pattern === SignaturePatternEnum.SIGN_WITHOUT_LOGO
                  ? true
                  : undefined,
              name: signature.name,
              isMoved: signature.isMoved,
              signatoryEmail: signature.email,
            });
          });
      } else if (transactionType === "scellement" && signatures.length > 0) {
        signFields.signFields.push({
          positionX: (signatures[0].positionX ?? 0) / window.devicePixelRatio,
          positionY: (signatures[0].positionY ?? 0) / window.devicePixelRatio,
          signaturePriorityRule: signatures[0].signaturePriorityRule,
          signaturePictureRank: signatures[0].signaturePictureRank,
          page: signatures[0].page,
          pattern: signatures[0].pattern,
          withoutLogo:
            signatures[0].pattern === SignaturePatternEnum.SEAL_WITHOUT_LOGO
              ? true
              : undefined,
          signatoryEmail: isFromModel ? "scellement" : signatures[0].email,
          name: isFromModel ? "scellement" : undefined,
          isMoved: signatures[0].isMoved,
        });
      }

      const documentOrder = key + 1;
      body.append("files[]", file.file);
      body.append("documentOrders[]", JSON.stringify(documentOrder));

      signFieldsArray.push(signFields);

      if (initialFields) {
        initialFieldsArray.push({ ...initialFields, documentOrder });
      }
    });

    body.append("signFields", JSON.stringify(signFieldsArray));
    body.append("initialFields", JSON.stringify(initialFieldsArray));

    return body;
  };

  static checkIfPdfIsEditable = (pdfUrl: string): Promise<boolean> => {
    return fetch(pdfUrl)
      .then((response) => response.arrayBuffer())
      .then((existingPdfBytes) => PDFDocument.load(existingPdfBytes))
      .then((pdfDoc) => {
        const form = pdfDoc.getForm();
        const fields = form.getFields();
        return fields.length > 0 && !FileHelper.hasSignature(form);
      })
      .catch((error) => {
        console.error("An error occurred while checking the PDF:", error);
        return false;
      });
  };

  static hasSignature = (form: PDFForm): boolean => {
    return form
      .getFields()
      ?.map((field) => field instanceof PDFSignature)
      .some((p) => p);
  };

  static freezePdf = async (existingFile: File): Promise<File> => {
    const type = "application/pdf";

    if (!existingFile.url) {
      throw new Error("File URL is missing.");
    }

    // Fetch the existing PDF from the URL
    const existingPdfBytes = await fetch(existingFile.url).then((response) =>
      response.arrayBuffer(),
    );

    // Load the PDF and flatten the form fields
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const form = pdfDoc.getForm();

    form.flatten(); // Make the PDF non-editable

    // Remove acroform
    pdfDoc.catalog.delete(PDFName.of("AcroForm"));

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type });

    const newFile = new globalThis.File(
      [blob],
      existingFile.fileName || existingFile.name,
      { type },
    );

    const base64Data = await blobToBase64(blob);

    return {
      ...existingFile,
      base64Data: base64Data,
      size: newFile.size,
      type: newFile.type,
      url: URL.createObjectURL(newFile),
      file: newFile,
    };
  };

  static createDownloadFileFromBlob = (
    blob: Blob,
    defaultFilename: string,
    headers?: Headers,
  ): void => {
    const disposition = headers?.get("content-disposition") ?? "";
    let filename = defaultFilename;
    const match = disposition.match(
      /filename\*=UTF-8''(.+)$|filename="?([^"]+)"?/,
    );
    if (match) {
      filename = decodeURIComponent(match[1] || match[2]);
    }

    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(objectUrl);
  };

  static handleFileChange = async (fileUpload: FileUpload): Promise<File[]> => {
    const {
      maxCount,
      selectedFiles,
      currentFileCount,
      extensions,
      accept,
      maxSize,
      maxSizeOfAllFiles,
      currentDocumentsSize,
    } = fileUpload;
    const needToCheckFilesCount =
      maxCount != undefined && currentFileCount != undefined;
    if (
      needToCheckFilesCount &&
      selectedFiles.length + currentFileCount > maxCount
    ) {
      throw new Error(t("error.uploadFiles.nbFile", { nbFile: maxCount }));
    }

    let sum = 0;
    const invalidFiles: File[] = [];
    let valid = true;
    let filenameTooLong = false;
    let filesSizeTooLong = false;
    let fileSize = 0;
    let fileType = "";

    const base64Promises = Array.from(selectedFiles).map(async (file) => {
      if (
        (file.type !== accept &&
          extensions?.indexOf(file.name.split(".").pop() as string) === -1) ||
        file.name.length > MAX_FILE_NAME_LENGTH
      ) {
        fileType = file.type;
        valid = false;
        if (file.name.length > MAX_FILE_NAME_LENGTH) {
          filenameTooLong = true;
        }
      }

      const base64 = await getBase64Async(file);
      const customFile: File = {
        key: file.name + Date.now().toString(),
        name: file.name,
        size: file.size,
        base64Data: base64,
        type: file.type,
        url: URL.createObjectURL(file),
        file: file,
      };

      return customFile;
    });

    if (!valid) {
      let error: string = "";
      if (filenameTooLong) {
        error = t("error.uploadFiles.filenameTooLong");
      } else if (!extensions?.some((ext) => fileType.includes(ext))) {
        error = t("error.uploadFiles.extension", { accept });
      } else {
        error = t("error.uploadFiles.unknown");
      }
      throw new Error(error);
    }

    const customFiles = await Promise.all(base64Promises);

    const filesToAdd: File[] = [];

    for (const fileToAdd of customFiles) {
      if (fileToAdd.type === accept && fileToAdd.size) {
        sum += fileToAdd.size;

        let isPdfEditable = false;

        try {
          isPdfEditable = await FileHelper.checkIfPdfIsEditable(
            fileToAdd.url ?? "",
          );
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
          isPdfEditable = true;
        }

        if (maxSize && fileToAdd.size + currentDocumentsSize > maxSize) {
          invalidFiles.push(fileToAdd);
        } else if (
          maxSizeOfAllFiles &&
          sum + currentDocumentsSize > maxSizeOfAllFiles
        ) {
          filesSizeTooLong = true;
        } else if (!isPdfEditable) {
          // Add the non-editable PDF to the list
          filesToAdd.push(fileToAdd);
          fileSize = fileSize + fileToAdd.size;
        } else {
          // filesToAdd.push(fileToAdd);
          // fileSize = fileSize + fileToAdd.size;
          // Avec UTC il semblerait qu'il ne soit plus nécessaire de flatten les pdf avant de les envoyer
          // au contraire il semblerait même que cela provoque des erreurs
          // // if the PDF is editable, freeze it to make in non-editable and avoid universign error
          console.log("The PDF is editable, handle accordingly.");
          try {
            const file = await FileHelper.freezePdf(fileToAdd);
            filesToAdd.push(file);
            fileSize = fileSize + fileToAdd.size;
          } catch (error) {
            console.error(
              "Une erreur est survenue lors de FileHelper.freezePdf:",
              error,
            );
          }
        }
      }
    }

    let error: string | undefined = undefined;
    if (filesSizeTooLong) {
      error = t("error.uploadFiles.fileSizeTooLong");
    } else if (invalidFiles.length === customFiles.length) {
      error = t("error.uploadFiles.totalSize");
    } else if (invalidFiles.length > 0) {
      error = t("error.uploadFiles.oneOrManyDocumentTooBig");
    }

    if (error) {
      throw new Error(error);
    }

    return filesToAdd;
  };

  // Wrapper to handle the change event
  static handleFileInputChange = (
    setFiles: (files: File[]) => void,
    fileUpload: FileUpload,
  ): void => {
    if (fileUpload.selectedFiles) {
      this.handleFileChange(fileUpload)
        .then((files: File[]) => {
          setFiles(files);
        })
        .catch((error: Error) => {
          toastError(error.message);
        });
    }
  };
}
